import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { CallRailForm } from 'app/components/integrations/setup-forms/CallRailForm'
import { CarestackWorkspaceModalForm } from 'app/components/integrations/setup-forms/CarestackWorkspaceModalForm'
import { FacebookWorkspaceModalForm } from 'app/components/integrations/setup-forms/FacebookWorkspaceModalForm'
import { AscendWorkspaceModalForm } from 'app/components/integrations/setup-forms/AscendWorkspaceModalForm'
import { GravityFormWorkspaceModal } from 'app/components/integrations/setup-forms/GravityFormWorkspaceModal'
import { JotformWorkspaceForm } from 'app/components/integrations/setup-forms/JotformWorkspaceForm'
import { RingCentralForm } from 'app/components/integrations/setup-forms/RingCentralForm'
import { TwilioForm } from 'app/components/integrations/setup-forms/TwilioForm'
import { FacebookAdsFormModal } from 'app/components/integrations/configuration-forms/FacebookAdsFormModal'
import { FacebookLeadsFormModal } from 'app/components/integrations/configuration-forms/FacebookLeadsFormModal'
import { consumeWizardResume, type IntegrationName } from 'app/components/integrations/workspaceRequirements'
import { PageScroll } from 'app/components/layout/PageScroll'
import SimpleTable from 'app/components/table/SimpleTable'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { defaultWorkspaceColumns, filterableWorkspaceColumns } from 'app/table-columns/integrations'
import { useWorkspace, useUser } from 'app/utils'
import { debug } from 'lib/logging'
import type { Jsonify } from 'lib/toJSON'
import type { WorkspaceIntegrationsRow } from 'app/types/integrations'

export const handle = {
  title: `Workspace`,
  breadcrumb: `Workspace`,
}
export type { WorkspaceIntegrationsRow } from 'app/types/integrations'

export default function WorkspaceIntegrationsComponent() {
  const [results, setResults] = useState<WorkspaceIntegrationsRow[]>([])
  const [searchValue, setSearchValue] = useState(``)
  const [searchParams, setSearchParams] = useSearchParams()
  const workspace = useWorkspace()
  const user = useUser()

  function fetchData() {
    fetch(`/api/integrations/workspace-list`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => setResults(data.results ?? []))
  }

  useEffect(() => {
    fetchData()
  }, [])

  function onIntegrationUpdated() {
    fetchData()
  }

  const onIntegrationClick = (integrationsRow: Jsonify<WorkspaceIntegrationsRow>) => {
    if (integrationsRow.integration === `Carestack`) {
      showModal(
        <CarestackWorkspaceModalForm
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={{
            ...integrationsRow.carestackConfig,
          }}
        />,
        {
          title: `Carestack Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `RingCentral`) {
      showModal(
        <RingCentralForm
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={{
            ...integrationsRow.ringCentralConfig,
          }}
        />,
        {
          title: `RingCentral Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `CallRail`) {
      showModal(
        <CallRailForm
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={{
            ...integrationsRow.callRailConfig,
          }}
        />,
        {
          title: `CallRail Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Twilio`) {
      showModal(
        <TwilioForm
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={{
            ...integrationsRow.twilioConfig,
          }}
        />,
        {
          title: `Twilio Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Facebook Ads`) {
      showModal(
        <FacebookWorkspaceModalForm
          onIntegrationChanged={onIntegrationUpdated}
          integrationType="facebook-ads"
          currentValues={{
            ...integrationsRow.facebookConfig,
            lastUpdated: integrationsRow.lastUpdate,
          }}
        />,
        {
          title: `Facebook Ads Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Facebook Leads`) {
      showModal(
        <FacebookWorkspaceModalForm
          onIntegrationChanged={onIntegrationUpdated}
          integrationType="facebook-leads"
          currentValues={{
            ...integrationsRow.facebookConfig,
            lastUpdated: integrationsRow.lastUpdate,
          }}
        />,
        {
          title: `Facebook Leads Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Gravity Forms`) {
      showModal(
        <GravityFormWorkspaceModal
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={integrationsRow.gravityFormsMultiConfig}
        />,
        {
          title: `Gravity Forms Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Ascend`) {
      showModal(
        <AscendWorkspaceModalForm
          onIntegrationChanged={onIntegrationUpdated}
          currentValues={{
            ...integrationsRow.ascendConfig,
          }}
        />,
        {
          title: `Ascend Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
    if (integrationsRow.integration === `Jotform`) {
      showModal(
        <JotformWorkspaceForm
          onIntegrationChanged={onIntegrationUpdated}
          orgId={user?.orgId}
          currentValues={{
            ...integrationsRow.jotformConfig,
          }}
        />,
        {
          title: `JotForm Configuration`,
          isFullScreen: false,
          overflowHidden: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }
  }
  const columns = useMemo(() => defaultWorkspaceColumns(onIntegrationClick), [onIntegrationClick])
  const filters = useMemo(() => filterableWorkspaceColumns(), [results])

  debug(`integrations`, `workspace data`, results)

  const filteredRows = useClientSideTableFilters(results, filters, `key`, [`status`, `integration`], searchValue)

  useEffect(() => {
    const success = searchParams.get(`success`)
    if (success === `facebook_ads_connected` || success === `facebook_leads_connected`) {
      const connected: IntegrationName = success === `facebook_ads_connected` ? `Facebook Ads` : `Facebook Leads`
      notifySuccess(`${connected} successfully connected!`)
      const resume = consumeWizardResume()
      if (resume === connected) {
        const onAdd = () => window.dispatchEvent(new CustomEvent(`integrations:refetch`))
        showModal(
          connected === `Facebook Ads` ? (
            <FacebookAdsFormModal onAdd={onAdd} />
          ) : (
            <FacebookLeadsFormModal onAdd={onAdd} />
          ),
          {
            title: `${connected} — Step 2 of 2`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
      }
      searchParams.delete(`success`)
      setSearchParams(searchParams)
    } else if (searchParams.has(`error`)) {
      // An aborted wizard OAuth round-trip must not resume a later, unrelated connect.
      consumeWizardResume()
      switch (searchParams.get(`error`)) {
        case `missing_state`:
          notifyError(`Server error: Missing state`)
          break
        case `missing_workspace_id`:
          notifyError(`Server error: Missing workspace id`)
          break
        case `missing_code`:
          notifyError(`Server error: Missing code`)
          break
        default:
          notifyError(`Server error: ${searchParams.get(`error`)}`)
          break
      }
    }
  }, [searchParams])

  return (
    <PageScroll variant="table">
      <SimpleTable
        className="grow"
        hideFooter
        columns={columns as any}
        data={filteredRows}
        filterableColumns={filters}
        onSearchValueChange={(val) => setSearchValue(val)}
        edgePadded
      />
    </PageScroll>
  )
}
